
import {
  NotificationsOff,
  ErrorOutline,
  WarningAmber,
  InfoOutlined,
} from "@mui/icons-material";
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, 
  Paper, Chip, Avatar, IconButton, TextField, InputAdornment, Dialog,
  DialogTitle, DialogContent, DialogActions, Button, Tabs, Tab, Box, Typography,
  CircularProgress, Snackbar, Alert, Badge, Tooltip
} from '@mui/material';
import { 
  Search, Phone, Email, AssignmentInd, Close, CheckCircle, Refresh, 
  SwapHoriz, Notifications, LocationOn, Circle
} from '@mui/icons-material';
import axios from 'axios';
import io from 'socket.io-client';
import './DeliveryPersons.css';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'https://api.rismicmart.com';
const API_URL = process.env.REACT_APP_BASE_URL || 'https://api.rismicmart.com';

const ActiveDeliveryPersons = () => {
  // State variables
  const [activePersons, setActivePersons] = useState([]);
  const [filteredPersons, setFilteredPersons] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [orderSearchQuery, setOrderSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  
  // Assignment modal states
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedDeliveryPerson, setSelectedDeliveryPerson] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [isReassignment, setIsReassignment] = useState(false);
  
  // Notification states
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [realtimeNotifications, setRealtimeNotifications] = useState([]);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [notificationOpen, setNotificationOpen] = useState(false);
  
  // Socket connection states
  const [socketConnected, setSocketConnected] = useState(false);
  const [onlineDeliveryPersons, setOnlineDeliveryPersons] = useState(new Set());
  const [deliveryLocations, setDeliveryLocations] = useState(new Map());
  const [locationAddresses, setLocationAddresses] = useState(new Map());
  
  // Refs
  const socketRef = useRef(null);
  const audioRef = useRef(null);
  const adminId = useRef(`admin-${Date.now()}`);
  const geocodingCache = useRef(new Map());

  // ==================== GEOCODING FUNCTION ====================
  
  const getAddressFromCoordinates = async (lat, lng, personId) => {
    const cacheKey = `${lat.toFixed(5)},${lng.toFixed(5)}`;
    
    if (geocodingCache.current.has(cacheKey)) {
      return geocodingCache.current.get(cacheKey);
    }

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
        {
          headers: {
            'User-Agent': 'DeliveryManagementApp/1.0'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        const address = data.display_name || data.address?.road || 'Location unavailable';
        
        geocodingCache.current.set(cacheKey, address);
        
        setLocationAddresses(prev => {
          const newMap = new Map(prev);
          newMap.set(String(personId), address);
          return newMap;
        });
        
        return address;
      }
    } catch (error) {
      // Silently fail
    }
    
    return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
  };

  // ==================== FETCH ONLINE STATUS ====================
  
  const fetchOnlineStatus = useCallback(async () => {
    try {
      if (socketRef.current && socketRef.current.connected) {
        socketRef.current.emit('get-online-delivery-persons');
        socketRef.current.emit('get-all-delivery-locations');
      }
    } catch (error) {
      // Handle error silently
    }
  }, []);

  // ==================== SOCKET.IO SETUP ====================
  
  useEffect(() => {
    audioRef.current = new Audio('/notification-sound.mp3');
    audioRef.current.volume = 0.5;

    socketRef.current = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    const socket = socketRef.current;

    socket.on('connect', () => {
      setSocketConnected(true);
      socket.emit('register-admin', adminId.current);
      
      setTimeout(() => {
        fetchOnlineStatus();
      }, 500);
      
      showSnackbar('Connected to real-time updates', 'success');
    });

    socket.on('connect_error', (error) => {
      setSocketConnected(false);
      showSnackbar('Connection error. Retrying...', 'warning');
    });

    socket.on('disconnect', (reason) => {
      setSocketConnected(false);
      showSnackbar('Disconnected from real-time updates', 'error');
    });

    socket.on('admin:registered', (data) => {
      showSnackbar('Real-time notifications enabled', 'success');
      fetchOnlineStatus();
    });

    // ==================== RECEIVE ONLINE STATUS LIST ====================
    
    socket.on('online-delivery-persons-list', (data) => {
      if (data && data.onlinePersons) {
        const onlineSet = new Set(data.onlinePersons.map(id => String(id)));
        setOnlineDeliveryPersons(onlineSet);
      }
    });

    // ==================== RECEIVE ALL DELIVERY LOCATIONS ====================
    
    socket.on('all-delivery-locations', (data) => {
      console.log('📍 Received all delivery locations:', data);
      
      if (data && data.locations) {
        const newLocationsMap = new Map();
        
        data.locations.forEach(locData => {
          const { deliveryPersonId, location, timestamp, orderId, speed, heading } = locData;
          
          if (deliveryPersonId && location) {
            const lat = location.lat || location.latitude;
            const lng = location.lng || location.longitude;
            
            if (lat && lng) {
              newLocationsMap.set(String(deliveryPersonId), {
                lat: lat,
                lng: lng,
                orderId: orderId,
                speed: speed || 0,
                heading: heading || 0,
                timestamp: timestamp || new Date().toISOString()
              });
              
              getAddressFromCoordinates(lat, lng, deliveryPersonId);
            }
          }
        });
        
        setDeliveryLocations(newLocationsMap);
        console.log(`✅ Loaded ${newLocationsMap.size} delivery locations`);
      }
    });

    // ==================== DELIVERY PERSON EVENTS ====================

    socket.on('delivery-person-online', (data) => {
      const personId = data.deliveryPersonId || data.userId || data.id;
      if (personId) {
        setOnlineDeliveryPersons(prev => new Set(prev).add(String(personId)));
        addNotification({
          type: 'info',
          message: `Delivery person came online`,
          timestamp: data.timestamp,
          deliveryPersonId: personId
        });
      }
    });

    socket.on('delivery-person-offline', (data) => {
      const personId = data.deliveryPersonId || data.userId || data.id;
      if (personId) {
        setOnlineDeliveryPersons(prev => {
          const newSet = new Set(prev);
          newSet.delete(String(personId));
          return newSet;
        });
        addNotification({
          type: 'warning',
          message: `Delivery person went offline`,
          timestamp: data.timestamp,
          deliveryPersonId: personId
        });
      }
    });

    // ==================== ORDER ASSIGNMENT EVENTS ====================

    socket.on('order:assigned-admin', (data) => {
      playNotificationSound();
      addNotification({
        type: 'success',
        message: `Order #${data.orderId.slice(-6)} assigned to delivery person`,
        timestamp: data.timestamp,
        orderId: data.orderId,
        deliveryPersonId: data.deliveryPersonId
      });
      fetchAllOrders();
    });

    socket.on('order:accepted-admin', (data) => {
      playNotificationSound();
      addNotification({
        type: 'success',
        message: `Order #${data.orderId.slice(-6)} accepted by delivery person`,
        timestamp: data.timestamp,
        orderId: data.orderId,
        deliveryPersonId: data.deliveryPersonId
      });
      updateOrderInState(data.orderId, { status: 'processing' });
    });

    socket.on('order:rejected-admin', (data) => {
      playNotificationSound();
      addNotification({
        type: 'error',
        message: `Order #${data.orderId.slice(-6)} rejected by ${data.deliveryPersonName}`,
        reason: data.rejectionReason,
        timestamp: data.timestamp,
        orderId: data.orderId,
        deliveryPersonId: data.deliveryPersonId
      });
      updateOrderInState(data.orderId, { 
        status: 'pending', 
        deliveryPersonId: null 
      });
    });

    // ==================== ORDER STATUS EVENTS ====================

    socket.on('order:status-changed', (data) => {
      const statusMessages = {
        'pending': 'returned to pending',
        'confirmed': 'confirmed',
        'processing': 'being processed',
        'shipped': 'shipped',
        'delivered': 'delivered',
        'cancelled': 'cancelled'
      };

      addNotification({
        type: data.status === 'cancelled' ? 'error' : 
              data.status === 'delivered' ? 'success' : 'info',
        message: `Order #${data.orderId.slice(-6)} ${statusMessages[data.status] || 'updated'}`,
        timestamp: data.timestamp,
        orderId: data.orderId,
        status: data.status
      });

      updateOrderInState(data.orderId, { 
        status: data.status,
        ...(data.action === 'rejected' && { deliveryPersonId: null })
      });
    });

    socket.on('order:cancelled', (data) => {
      playNotificationSound();
      addNotification({
        type: 'error',
        message: `Order #${data.orderId.slice(-6)} cancelled`,
        reason: data.reason,
        cancelledBy: data.cancelledBy,
        timestamp: data.timestamp,
        orderId: data.orderId
      });
      updateOrderInState(data.orderId, { status: 'cancelled' });
    });

    socket.on('order:delivered', (data) => {
      playNotificationSound();
      addNotification({
        type: 'success',
        message: `Order #${data.orderId.slice(-6)} delivered successfully!`,
        timestamp: data.timestamp,
        orderId: data.orderId,
        deliveryPersonId: data.deliveryPersonId
      });
      updateOrderInState(data.orderId, { status: 'delivered' });
    });

    // ==================== LOCATION TRACKING ====================

    const locationEvents = [
      'delivery:location-update-admin',
      'location-update',
      'deliveryLocationUpdate',
      'delivery-location-update'
    ];

    locationEvents.forEach(eventName => {
      socket.on(eventName, (data) => {
        const personId = data.deliveryPersonId || data.userId || data.id;
        const locationData = data.location || data;
        
        if (personId && locationData) {
          const lat = locationData.lat || locationData.latitude;
          const lng = locationData.lng || locationData.longitude;
          
          if (lat && lng) {
            setDeliveryLocations(prev => {
              const newMap = new Map(prev);
              newMap.set(String(personId), {
                lat: lat,
                lng: lng,
                orderId: data.orderId,
                speed: data.speed,
                heading: data.heading,
                timestamp: data.timestamp || new Date().toISOString()
              });
              return newMap;
            });

            getAddressFromCoordinates(lat, lng, personId);
          }
        }
      });
    });

    return () => {
      socket.off('connect');
      socket.off('disconnect');
      socket.off('connect_error');
      socket.off('admin:registered');
      socket.off('online-delivery-persons-list');
      socket.off('all-delivery-locations');
      socket.off('delivery-person-online');
      socket.off('delivery-person-offline');
      socket.off('order:assigned-admin');
      socket.off('order:accepted-admin');
      socket.off('order:rejected-admin');
      socket.off('order:status-changed');
      socket.off('order:cancelled');
      socket.off('order:delivered');
      locationEvents.forEach(eventName => socket.off(eventName));
      socket.disconnect();
    };
  }, [fetchOnlineStatus]);

  // ==================== HELPER FUNCTIONS ====================

  const playNotificationSound = () => {
    if (audioRef.current) {
      audioRef.current.play().catch(err => {});
    }
  };

  const addNotification = (notification) => {
    const newNotification = {
      id: Date.now(),
      ...notification,
      timestamp: notification.timestamp || new Date().toISOString(),
      read: false
    };

    setRealtimeNotifications(prev => [newNotification, ...prev].slice(0, 50));
    setUnreadNotifications(prev => prev + 1);

    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('Admin Alert', {
        body: notification.message,
        icon: '/logo192.png',
        tag: notification.orderId || 'admin-notification'
      });
    }
  };

  const updateOrderInState = (orderId, updates) => {
    setAllOrders(prev => 
      prev.map(order => 
        order._id === orderId ? { ...order, ...updates } : order
      )
    );
    
    setFilteredOrders(prev => 
      prev.map(order => 
        order._id === orderId ? { ...order, ...updates } : order
      )
    );
  };

  const markNotificationsAsRead = () => {
    setRealtimeNotifications(prev => 
      prev.map(notif => ({ ...notif, read: true }))
    );
    setUnreadNotifications(0);
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // ==================== DATA FETCHING ====================

  const fetchActivePersons = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/delivery-person`);
      
      if (response.data.success) {
        const allPersons = response.data.data;
        const active = allPersons.filter(person => person.isActive);
        setActivePersons(active);
        setFilteredPersons(active);
        return active;
      }
    } catch (error) {
      showSnackbar('Failed to load delivery persons', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAllOrders = useCallback(async () => {
    try {
      setOrdersLoading(true);
      const response = await axios.get(`${API_URL}/api/orders`);
      setAllOrders(response.data);
      setFilteredOrders(response.data);
      return response.data;
    } catch (error) {
      showSnackbar('Failed to load orders', 'error');
    } finally {
      setOrdersLoading(false);
    }
  }, []);

  const loadData = useCallback(async () => {
    await fetchActivePersons();
    await fetchAllOrders();
    fetchOnlineStatus();
  }, [fetchActivePersons, fetchAllOrders, fetchOnlineStatus]);

  useEffect(() => {
    loadData();
    
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, [loadData]);

  // ==================== SEARCH FILTERING ====================

  useEffect(() => {
    if (searchQuery) {
      const filtered = activePersons.filter(person =>
        person.fullName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        person.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        person.phone?.includes(searchQuery)
      );
      setFilteredPersons(filtered);
    } else {
      setFilteredPersons(activePersons);
    }
  }, [searchQuery, activePersons]);

  useEffect(() => {
    if (orderSearchQuery) {
      const filtered = allOrders.filter(order =>
        order.name?.toLowerCase().includes(orderSearchQuery.toLowerCase()) ||
        order.phoneNumber?.includes(orderSearchQuery) ||
        order.address?.toLowerCase().includes(orderSearchQuery.toLowerCase())
      );
      setFilteredOrders(filtered);
    } else {
      setFilteredOrders(allOrders);
    }
  }, [orderSearchQuery, allOrders]);

  // ==================== ASSIGNMENT HANDLERS ====================

  const handleOpenAssignModal = (order, isReassign = false) => {
    setSelectedOrder(order);
    setSelectedDeliveryPerson(order.deliveryPersonId || '');
    setIsReassignment(isReassign);
    setAssignModalOpen(true);
  };

  const handleCloseAssignModal = () => {
    setAssignModalOpen(false);
    setSelectedOrder(null);
    setSelectedDeliveryPerson('');
    setIsReassignment(false);
  };

  const handleAssignDeliveryPerson = async () => {
    if (!selectedDeliveryPerson) {
      showSnackbar('Please select a delivery person', 'warning');
      return;
    }

    if (!selectedOrder) {
      showSnackbar('Order not found', 'error');
      return;
    }

    setAssigning(true);
    
    try {
      const updateData = {
        deliveryPersonId: selectedDeliveryPerson,
        status: 'confirmed'
      };

      const response = await axios.put(
        `${API_URL}/api/orders/${selectedOrder._id}`,
        updateData,
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.data && response.data._id) {
        const assignedPerson = activePersons.find(p => p._id === selectedDeliveryPerson);
        
        if (socketRef.current && socketRef.current.connected) {
          socketRef.current.emit('order:assigned', {
            orderId: selectedOrder._id,
            deliveryPersonId: selectedDeliveryPerson,
            order: {
              ...selectedOrder,
              deliveryPersonId: selectedDeliveryPerson,
              status: 'confirmed'
            }
          });
        }
        
        updateOrderInState(selectedOrder._id, {
          deliveryPersonId: selectedDeliveryPerson,
          status: 'confirmed'
        });
        
        handleCloseAssignModal();
        
        const actionText = isReassignment ? 'reassigned to' : 'assigned to';
        showSnackbar(`✓ Successfully ${actionText} ${assignedPerson?.fullName || 'delivery person'}!`, 'success');
        
        setTimeout(async () => {
          await loadData();
        }, 500);
      }
    } catch (error) {
      showSnackbar(`Failed to ${isReassignment ? 'reassign' : 'assign'}: ${error.response?.data?.error || error.message}`, 'error');
    } finally {
      setAssigning(false);
    }
  };

  const handleUnassign = async (order) => {
    if (!window.confirm(`Are you sure you want to unassign the delivery person from order #${order._id.slice(-6).toUpperCase()}?`)) {
      return;
    }

    try {
      const response = await axios.put(
        `${API_URL}/api/orders/${order._id}`,
        {
          deliveryPersonId: null,
          status: 'pending'
        }
      );

      if (response.data) {
        if (socketRef.current && socketRef.current.connected) {
          socketRef.current.emit('order:status-update', {
            orderId: order._id,
            status: 'pending',
            updateType: 'unassignment'
          });
        }

        showSnackbar('✓ Delivery person unassigned successfully!', 'success');
        await loadData();
      }
    } catch (error) {
      showSnackbar(`Failed to unassign: ${error.response?.data?.error || error.message}`, 'error');
    }
  };

  // ==================== UI HELPER FUNCTIONS ====================

  const getStatusColor = (status) => {
    switch (status) {
      case 'Available': return 'success';
      case 'Busy': return 'warning';
      case 'Offline': return 'default';
      default: return 'default';
    }
  };

  const getOrderStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'confirmed': return 'info';
      case 'processing': return 'primary';
      case 'shipped': return 'secondary';
      case 'delivered': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getOrderStatusLabel = (status) => {
    return status?.charAt(0).toUpperCase() + status?.slice(1) || 'Unknown';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN') + ' ' + date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  const getDeliveryPersonInfo = (deliveryPersonId) => {
    if (!deliveryPersonId) {
      return null;
    }

    const targetId = String(deliveryPersonId);
    const person = activePersons.find(p => String(p._id) === targetId);

    return person;
  };

  const isDeliveryPersonOnline = (deliveryPersonId) => {
    return onlineDeliveryPersons.has(String(deliveryPersonId));
  };

  const getDeliveryPersonLocation = (deliveryPersonId) => {
    return deliveryLocations.get(String(deliveryPersonId));
  };

  const getDeliveryPersonAddress = (deliveryPersonId) => {
    return locationAddresses.get(String(deliveryPersonId));
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // ==================== DATA CALCULATIONS ====================

  const onlinePersons = activePersons.filter(p => p.status === 'Available');
  const busyPersons = activePersons.filter(p => p.status === 'Busy');
  
  const pendingOrders = filteredOrders.filter(o => o.status === 'pending');
  const unassignedOrders = filteredOrders.filter(o => !o.deliveryPersonId && o.status !== 'delivered' && o.status !== 'cancelled');
  const assignedOrders = filteredOrders.filter(o => o.deliveryPersonId && (o.status === 'confirmed' || o.status === 'processing'));
  const activeDeliveryOrders = filteredOrders.filter(o => o.status === 'shipped');
  const completedOrders = filteredOrders.filter(o => o.status === 'delivered');

  // ==================== RENDER ====================

  return (
    <div className="delivery-persons-page">
      <div className="page-header">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <h1>Order & Delivery Management</h1>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Tooltip title={socketConnected ? 'Connected to real-time updates' : 'Disconnected'}>
              <Chip
                icon={<Circle sx={{ fontSize: 12 }} />}
                label={socketConnected ? 'Live' : 'Offline'}
                color={socketConnected ? 'success' : 'default'}
                size="small"
                variant="outlined"
              />
            </Tooltip>

            <Tooltip title="View notifications">
              <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <Box sx={{ position: 'relative' }}>
                  <IconButton 
                    color="primary" 
                    onClick={() => {
                      setNotificationOpen(!notificationOpen);
                      if (!notificationOpen) {
                        markNotificationsAsRead();
                      }
                    }}
                    sx={{ 
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        border: `3px solid ${unreadNotifications > 0 ? '#ef4444' : 'transparent'}`,
                        transform: 'translate(-50%, -50%)',
                        boxShadow: unreadNotifications > 0 ? '0 0 20px rgba(239, 68, 68, 0.4)' : 'none',
                      }
                    }}
                  >
                    <Badge badgeContent={unreadNotifications > 99 ? '99+' : unreadNotifications} color="error" sx={{ '& .MuiBadge-badge': { fontSize: '10px', minWidth: '18px', height: '18px' } }}>
                      <Notifications />
                    </Badge>
                  </IconButton>
                  
                  {unreadNotifications > 0 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        width: 56,
                        height: 56,
                        borderRadius: '50%',
                        background: 'rgba(239, 68, 68, 0.3)',
                        transform: 'translate(-50%, -50%)',
                        animation: 'notificationPulse 2s infinite',
                        zIndex: -1,
                      }}
                    />
                  )}
                </Box>

                {notificationOpen && (
                  <Paper
                    elevation={12}
                    sx={{
                      position: 'fixed',
                      bottom: 24,
                      right: 24,
                      width: 380,
                      maxHeight: 420,
                      borderRadius: 3,
                      overflow: 'hidden',
                      animation: 'slideUpNotification 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                      zIndex: 1500,
                      bgcolor: 'background.paper',
                      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                    }}
                  >
                    <Box sx={{ p: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                          📢 Live Notifications
                        </Typography>
                        <Chip 
                          label={`${realtimeNotifications.length}`} 
                          size="small" 
                          color="primary"
                          sx={{ fontWeight: 600 }}
                        />
                      </Box>
                      {realtimeNotifications.length > 0 && (
                        <Button
                          size="small"
                          onClick={() => setRealtimeNotifications([])}
                          sx={{ mt: 0.5 }}
                        >
                          Clear All
                        </Button>
                      )}
                    </Box>

                    <Box sx={{ maxHeight: 320, overflow: 'auto', p: 1.5 }}>
                      {realtimeNotifications.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                          <NotificationsOff sx={{ fontSize: 48, color: 'action.disabled', mb: 1 }} />
                          <Typography variant="body2">No notifications yet</Typography>
                          <Typography variant="caption">Everything running smoothly ✨</Typography>
                        </Box>
                      ) : (
                        realtimeNotifications.map((notif) => (
                          <Paper
                            key={notif.id}
                            elevation={0}
                            sx={{
                              p: 2,
                              mb: 1.5,
                              borderRadius: 2,
                              borderLeft: `4px solid`,
                              borderLeftColor: notif.type === 'success' ? 'success.main' : 
                                              notif.type === 'error' ? 'error.main' : 
                                              notif.type === 'warning' ? 'warning.main' : 'info.main',
                              bgcolor: `${notif.type}.light`,
                              '&:hover': { transform: 'translateX(4px)', transition: 'all 0.2s' },
                              cursor: 'pointer',
                            }}
                            onClick={() => {
                              setNotificationOpen(false);
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                              <Box sx={{ flexShrink: 0 }}>
                                {notif.type === 'success' && <CheckCircle color="success" />}
                                {notif.type === 'error' && <ErrorOutline color="error" />}
                                {notif.type === 'warning' && <WarningAmber color="warning" />}
                                {notif.type === 'info' && <InfoOutlined color="info" />}
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                                  {notif.message}
                                </Typography>
                                {notif.orderId && (
                                  <Typography variant="caption" sx={{ bgcolor: 'grey.900', color: 'grey.100', px: 1, py: 0.25, borderRadius: 1, fontWeight: 600 }}>
                                    #{notif.orderId.slice(-6).toUpperCase()}
                                  </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                                  {new Date(notif.timestamp).toLocaleTimeString()} • {new Date(notif.timestamp).toLocaleDateString()}
                                </Typography>
                                {notif.reason && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, fontStyle: 'italic' }}>
                                    {notif.reason}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </Paper>
                        ))
                      )}
                    </Box>
                  </Paper>
                )}
              </Box>
            </Tooltip>

            <Button 
              variant="outlined" 
              startIcon={<Refresh />} 
              onClick={loadData}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        <div className="header-stats">
          <div className="stat-card">
            <span className="stat-label">Total Active</span>
            <span className="stat-value">{activePersons.length}</span>
          </div>
          <div className="stat-card green">
            <span className="stat-label">Online</span>
            <span className="stat-value">{onlineDeliveryPersons.size}</span>
            <Circle sx={{ fontSize: 10, color: '#4caf50', ml: 1 }} />
          </div>
          <div className="stat-card yellow">
            <span className="stat-label">Busy</span>
            <span className="stat-value">{busyPersons.length}</span>
          </div>
          <div className="stat-card blue">
            <span className="stat-label">Total Orders</span>
            <span className="stat-value">{allOrders.length}</span>
          </div>
          <div className="stat-card orange">
            <span className="stat-label">Unassigned</span>
            <span className="stat-value">{unassignedOrders.length}</span>
          </div>
          <div className="stat-card purple">
            <span className="stat-label">In Transit</span>
            <span className="stat-value">{activeDeliveryOrders.length}</span>
          </div>
        </div>
      </div>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label={`All Orders (${filteredOrders.length})`} />
          <Tab 
            label={
              <Badge badgeContent={unassignedOrders.length} color="error">
                <span>Unassigned</span>
              </Badge>
            } 
          />
          <Tab label={`Pending (${pendingOrders.length})`} />
          <Tab label={`Assigned (${assignedOrders.length})`} />
          <Tab label={`Active Delivery (${activeDeliveryOrders.length})`} />
          <Tab label={`Completed (${completedOrders.length})`} />
          <Tab label={`Delivery Persons (${activePersons.length})`} />
        </Tabs>
      </Box>

      {tabValue !== 6 && (
        <>
          <div className="search-section">
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search orders by name, phone, or address..."
              value={orderSearchQuery}
              onChange={(e) => setOrderSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </div>

          <TableContainer component={Paper} className="table-container">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Order ID</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Items</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Delivery Person</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ordersLoading ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <CircularProgress size={40} />
                      <Typography sx={{ mt: 2 }}>Loading orders...</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  (() => {
                    let ordersToDisplay = filteredOrders;
                    if (tabValue === 1) ordersToDisplay = unassignedOrders;
                    else if (tabValue === 2) ordersToDisplay = pendingOrders;
                    else if (tabValue === 3) ordersToDisplay = assignedOrders;
                    else if (tabValue === 4) ordersToDisplay = activeDeliveryOrders;
                    else if (tabValue === 5) ordersToDisplay = completedOrders;

                    return ordersToDisplay.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} align="center">
                          <Typography color="text.secondary">No orders found</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      ordersToDisplay.map((order) => {
                        const deliveryPerson = getDeliveryPersonInfo(order.deliveryPersonId);
                        const isOnline = deliveryPerson && isDeliveryPersonOnline(deliveryPerson._id);
                        const location = deliveryPerson && getDeliveryPersonLocation(deliveryPerson._id);
                        const address = deliveryPerson && getDeliveryPersonAddress(deliveryPerson._id);
                        
                        return (
                          <TableRow key={order._id}>
                            <TableCell>
                              <strong>#{order._id.slice(-6).toUpperCase()}</strong>
                            </TableCell>
                            <TableCell>{order.name}</TableCell>
                            <TableCell>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <span style={{ fontSize: '12px' }}>
                                  <Phone style={{ fontSize: '14px', verticalAlign: 'middle' }} /> {order.phoneNumber}
                                </span>
                                <span style={{ fontSize: '12px' }}>
                                  <Email style={{ fontSize: '14px', verticalAlign: 'middle' }} /> {order.email}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                {order.address}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                PIN: {order.pincode}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <strong>₹{order.amount}</strong>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={`${order.products?.length || 0} items`} 
                                size="small" 
                                color="primary" 
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={getOrderStatusLabel(order.status)} 
                                color={getOrderStatusColor(order.status)} 
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {deliveryPerson ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{ position: 'relative' }}>
                                    <Avatar 
                                      src={deliveryPerson.profileImage?.url ? `${API_URL}${deliveryPerson.profileImage.url}` : undefined}
                                      sx={{ width: 32, height: 32 }}
                                    >
                                      {deliveryPerson.fullName?.charAt(0)}
                                    </Avatar>
                                    {isOnline && (
                                      <Circle 
                                        sx={{ 
                                          position: 'absolute', 
                                          bottom: 0, 
                                          right: 0, 
                                          fontSize: 12, 
                                          color: '#4caf50',
                                          backgroundColor: 'white',
                                          borderRadius: '50%'
                                        }} 
                                      />
                                    )}
                                  </Box>
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {deliveryPerson.fullName}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Typography variant="caption" color="text.secondary">
                                        {deliveryPerson.vehicleType}
                                      </Typography>
                                      {location && address && (
                                        <Tooltip title={address}>
                                          <LocationOn sx={{ fontSize: 14, color: '#ff9800', cursor: 'pointer' }} />
                                        </Tooltip>
                                      )}
                                    </Box>
                                  </Box>
                                </Box>
                              ) : (
                                <Box>
                                  <Typography variant="body2" color="error" sx={{ fontWeight: 500 }}>
                                    Not Assigned
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Click assign button
                                  </Typography>
                                </Box>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">{formatDate(order.date)}</Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                {order.deliveryPersonId ? (
                                  <>
                                    <IconButton 
                                      color="primary" 
                                      onClick={() => handleOpenAssignModal(order, true)}
                                      disabled={order.status === 'delivered' || order.status === 'cancelled'}
                                      title="Reassign Delivery Person"
                                      size="small"
                                    >
                                      <SwapHoriz />
                                    </IconButton>
                                    <Button
                                      variant="outlined"
                                      color="error"
                                      size="small"
                                      onClick={() => handleUnassign(order)}
                                      disabled={order.status === 'delivered' || order.status === 'cancelled'}
                                    >
                                      Unassign
                                    </Button>
                                  </>
                                ) : (
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    startIcon={<AssignmentInd />}
                                    onClick={() => handleOpenAssignModal(order, false)}
                                    disabled={order.status === 'delivered' || order.status === 'cancelled'}
                                  >
                                    Assign
                                  </Button>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    );
                  })()
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {tabValue === 6 && (
        <>
          <div className="search-section">
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by name, email, or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </div>

          <TableContainer component={Paper} className="table-container">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Profile</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Vehicle</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Current Location</TableCell>
                  <TableCell>Deliveries</TableCell>
                  <TableCell>Rating</TableCell>
                  <TableCell>Earnings</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredPersons.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">No active delivery persons found</TableCell>
                  </TableRow>
                ) : (
                  filteredPersons.map((person) => {
                    const isOnline = isDeliveryPersonOnline(person._id);
                    const location = getDeliveryPersonLocation(person._id);
                    const address = getDeliveryPersonAddress(person._id);
                    
                    return (
                      <TableRow key={person._id}>
                        <TableCell>
                          <div style={{ position: 'relative' }}>
                            <Avatar 
                              src={person.profileImage?.url ? `${API_URL}${person.profileImage.url}` : undefined}
                              alt={person.fullName}
                            >
                              {person.fullName?.charAt(0)}
                            </Avatar>
                            {isOnline && (
                              <div 
                                style={{
                                  position: 'absolute',
                                  bottom: 0,
                                  right: 0,
                                  width: 12,
                                  height: 12,
                                  backgroundColor: '#4caf50',
                                  borderRadius: '50%',
                                  border: '2px solid white'
                                }}
                              />
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{person.fullName}</TableCell>
                        <TableCell>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <span style={{ fontSize: '12px' }}>
                              <Phone style={{ fontSize: '14px', verticalAlign: 'middle' }} /> {person.phone}
                            </span>
                            <span style={{ fontSize: '12px' }}>
                              <Email style={{ fontSize: '14px', verticalAlign: 'middle' }} /> {person.email}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <span>{person.vehicleType}</span>
                            {person.vehicleNumber && (
                              <span style={{ fontSize: '12px', color: '#666' }}>{person.vehicleNumber}</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={person.status} 
                            color={getStatusColor(person.status)} 
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip
                                icon={<Circle sx={{ fontSize: 10 }} />}
                                label={isOnline ? 'Online' : 'Offline'}
                                color={isOnline ? 'success' : 'default'}
                                size="small"
                                variant="outlined"
                              />
                              {location && (
                                <LocationOn sx={{ fontSize: 18, color: '#ff9800' }} />
                              )}
                            </Box>
                            {location && (
                              <Box sx={{ fontSize: '11px', color: 'text.secondary', maxWidth: 250 }}>
                                <div style={{ fontWeight: 500, marginBottom: '4px' }}>
                                  {address || 'Loading address...'}
                                </div>
                                <div style={{ fontSize: '10px', color: '#999' }}>
                                  {new Date(location.timestamp).toLocaleTimeString()}
                                </div>
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <strong>{person.totalDeliveries || 0}</strong>
                        </TableCell>
                        <TableCell>
                          <span style={{ fontWeight: 'bold' }}>
                            {person.ratings?.average?.toFixed(1) || '0.0'}
                          </span>
                          <span style={{ fontSize: '12px', color: '#666' }}>
                            ({person.ratings?.count || 0})
                          </span>
                        </TableCell>
                        <TableCell>
                          <strong>₹{person.earnings || 0}</strong>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Dialog 
        open={assignModalOpen} 
        onClose={handleCloseAssignModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isReassignment ? <SwapHoriz color="primary" /> : <AssignmentInd color="primary" />}
            <span>{isReassignment ? 'Reassign Delivery Person' : 'Assign Delivery Person'}</span>
          </Box>
          <IconButton
            onClick={handleCloseAssignModal}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedOrder && (
            <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom>Order Details</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2">
                  <strong>Order ID:</strong> #{selectedOrder._id.slice(-6).toUpperCase()}
                </Typography>
                <Typography variant="body2">
                  <strong>Customer:</strong> {selectedOrder.name}
                </Typography>
                <Typography variant="body2">
                  <strong>Phone:</strong> {selectedOrder.phoneNumber}
                </Typography>
                <Typography variant="body2">
                  <strong>Address:</strong> {selectedOrder.address}
                </Typography>
                <Typography variant="body2">
                  <strong>Amount:</strong> ₹{selectedOrder.amount}
                </Typography>
                <Typography variant="body2">
                  <strong>Current Status:</strong>{' '}
                  <Chip 
                    label={getOrderStatusLabel(selectedOrder.status)} 
                    color={getOrderStatusColor(selectedOrder.status)} 
                    size="small"
                    sx={{ ml: 1 }}
                  />
                </Typography>
                {isReassignment && selectedOrder.deliveryPersonId && (
                  <Typography variant="body2" sx={{ mt: 1, p: 1, bgcolor: '#fff3cd', borderRadius: 1 }}>
                    <strong>Currently Assigned to:</strong> {getDeliveryPersonInfo(selectedOrder.deliveryPersonId)?.fullName || 'Unknown'}
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
              {isReassignment ? 'Select New Delivery Person' : 'Select Delivery Person'}
            </Typography>
            <select
              value={selectedDeliveryPerson}
              onChange={(e) => {
                setSelectedDeliveryPerson(e.target.value);
              }}
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '16px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                backgroundColor: 'white',
                cursor: 'pointer'
              }}
            >
              <option value="">-- Select a delivery person --</option>
              {activePersons.map((person) => {
                const isOnline = isDeliveryPersonOnline(person._id);
                return (
                  <option 
                    key={person._id} 
                    value={person._id}
                    disabled={person._id === selectedOrder?.deliveryPersonId}
                  >
                    {person.fullName} - {person.vehicleType} - {person.status} ({isOnline ? 'Online' : 'Offline'})
                    {person._id === selectedOrder?.deliveryPersonId ? ' (Currently Assigned)' : ''}
                  </option>
                );
              })}
            </select>
          </Box>

          {activePersons.length === 0 && (
            <Typography variant="body2" color="error" sx={{ mt: 2, p: 2, bgcolor: '#ffebee', borderRadius: 1 }}>
              No delivery persons available
            </Typography>
          )}

          {selectedDeliveryPerson && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle sx={{ color: '#1976d2' }} />
              <Box>
                <Typography variant="body2" color="primary">
                  {isReassignment ? 'New Assignment:' : 'Selected:'} {activePersons.find(p => p._id === selectedDeliveryPerson)?.fullName}
                </Typography>
                {isDeliveryPersonOnline(selectedDeliveryPerson) && (
                  <Typography variant="caption" color="success.main">
                    This delivery person is currently online
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button 
            onClick={handleCloseAssignModal} 
            disabled={assigning}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAssignDeliveryPerson}
            variant="contained"
            disabled={!selectedDeliveryPerson || assigning || selectedDeliveryPerson === selectedOrder?.deliveryPersonId}
            sx={{ minWidth: 120 }}
            startIcon={assigning ? <CircularProgress size={16} color="inherit" /> : (isReassignment ? <SwapHoriz /> : <AssignmentInd />)}
          >
            {assigning ? (isReassignment ? 'Reassigning...' : 'Assigning...') : (isReassignment ? 'Reassign Now' : 'Assign Now')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ActiveDeliveryPersons;

